import { Loader } from 'components';
import { AnimatePresence } from 'framer-motion';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Arrow, useLayer } from 'react-laag';
import { DeleteIcon, EditIcon, PDFIcon, ShareIcon, ThreeDotIcon } from 'static';

import {
  StyledCategory,
  StyledDivider,
  StyledLoaderWrapper,
  StyledMenuBody,
  StyledOption,
  StyledThreeDots,
  StyledTooltip,
} from './styled';

interface EventShareOptionsTooltipProps {
  zoom?: number;
  onShareClick: (e: Event) => void;
  onExportPDFClick?: (() => void) | null;
  onEditClick?: () => void;
  onDeleteClick?: () => void;
}

const EventShareOptionsTooltip: FC<EventShareOptionsTooltipProps> = ({
  zoom = 1,
  onShareClick,
  onExportPDFClick,
  onEditClick,
  onDeleteClick,
}) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const closeTooltip = () => setIsVisible(false);
  const hasManageActions = !!onEditClick || !!onDeleteClick;
  const hasExport = !!onExportPDFClick;

  const { triggerProps, layerProps, arrowProps, renderLayer } = useLayer({
    isOpen: isVisible,
    placement: 'right-start',
    triggerOffset: 10,
    auto: true,
    onOutsideClick: closeTooltip,
  });

  const handleIconClick = (e) => {
    e.stopPropagation();
    setIsVisible(!isVisible);
  };

  const handlePDFExport = async (e) => {
    e.stopPropagation();
    if (!onExportPDFClick) {
      return;
    }

    setLoading(true);
    await onExportPDFClick();
    setLoading(false);
    setIsVisible(false);
  };

  const handleShareClick = (e) => {
    e.stopPropagation();
    onShareClick(e);
    setIsVisible(false);
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    onEditClick?.();
    setIsVisible(false);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    onDeleteClick?.();
    setIsVisible(false);
  };

  return (
    <>
      <div {...triggerProps}>
        <StyledThreeDots
          type="button"
          zoom={zoom}
          onClick={handleIconClick}
          aria-label={t('common.more')}
          aria-expanded={isVisible}
        >
          <ThreeDotIcon />
        </StyledThreeDots>
      </div>
      {renderLayer(
        /* eslint-disable-next-line @typescript-eslint/ban-ts-comment*/
        /* @ts-ignore*/
        <AnimatePresence>
          {isVisible && (
            <StyledTooltip
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.15 }}
              {...layerProps}
              style={{
                ...layerProps.style,
              }}
              zoom={zoom}
            >
              <Arrow {...arrowProps} size={6} />
              <StyledMenuBody>
                {loading && (
                  <StyledLoaderWrapper>
                    <Loader size="30px" type={null} />
                  </StyledLoaderWrapper>
                )}

                {!!onEditClick && (
                  <>
                    <StyledCategory zoom={zoom}>
                      {t('event.menuCategory.manage')}
                    </StyledCategory>
                    <StyledOption zoom={zoom} onClick={handleEditClick}>
                      <EditIcon />
                      <label>{t('common.edit')}</label>
                    </StyledOption>
                  </>
                )}

                {hasManageActions && <StyledDivider zoom={zoom} />}

                <StyledCategory zoom={zoom}>
                  {t('event.menuCategory.share')}
                </StyledCategory>
                <StyledOption zoom={zoom} onClick={handleShareClick}>
                  <ShareIcon />
                  <label>{t('event.shareEventOption')}</label>
                </StyledOption>
                {hasExport && (
                  <StyledOption zoom={zoom} onClick={handlePDFExport}>
                    <PDFIcon />
                    <label>{t('event.exportPDFOption')}</label>
                  </StyledOption>
                )}

                {!!onDeleteClick && (
                  <>
                    <StyledDivider zoom={zoom} />
                    <StyledOption
                      zoom={zoom}
                      $danger
                      onClick={handleDeleteClick}
                    >
                      <DeleteIcon />
                      <label>{t('common.delete')}</label>
                    </StyledOption>
                  </>
                )}
              </StyledMenuBody>
            </StyledTooltip>
          )}
        </AnimatePresence>,
      )}
    </>
  );
};

export default EventShareOptionsTooltip;
